import React from 'react'
import {Flex, FlexProps, Text, TextProps, useMultiStyleConfig} from '@chakra-ui/react'
import {Link} from 'react-router-dom'
import {useAnalytics} from '_customHooks'
import {AnalyticsData} from '_types'
import {getLabel, getHierarchy} from '_analytics/utils/analyticsUtils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

interface menuButtonProps {
    variant: 'white' | 'underline' | 'footer'
    children: React.ReactNode
    href?: string
    isDisabled?: boolean
    closeMenu?: () => void
    analyticsEventData?: AnalyticsData
}

/**
 * Button component that renders a Chakra UI button with customizable properties.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {React.ElementType} props.as - The element or component to render as the button.
 * @param {string} [props.type='submit'] - The type of the button.
 * @param {string} [props.variant='primary'] - The variant of the button.
 * @param {string} [props.size='sm'] - The size of the button.
 * @param {string} [props.width='sm'] - The width of the button.
 * @param {boolean} [props.isDisabled=false] - Whether the button is disabled.
 * @param {function} props.onClick - The click event handler for the button.
 * @param {React.ReactNode} props.children - The content to be displayed inside the button.
 *
 * @returns {JSX.Element} The rendered button component.
 */
const MenuButton = ({variant, children, href, closeMenu, analyticsEventData}: menuButtonProps): JSX.Element => {
    const styles = useMultiStyleConfig('MenuButton', {variant}) as Record<string, FlexProps & TextProps>
    const {analyticsEventMenuClick} = useAnalytics()
    const {locale} = useMultiSite()
    const isExternalUrl = href?.startsWith('https://')
    const clickHandler = () => {
        if (analyticsEventData) {
            analyticsEventMenuClick({
                action: analyticsEventData?.mainMenu || '',
                label: getLabel(analyticsEventData),
                hierarchy: getHierarchy(analyticsEventData),
                menuTemplate: analyticsEventData?.analyticsTemplate || '',
            })
        }
        closeMenu?.()
    }
    return (
        <Flex
            as={isExternalUrl ? 'a' : Link}
            {...styles.linkBox}
            {...(isExternalUrl ? {href} : {to: `/${locale.alias}${href}`})}
            data-testid={`navigation-${variant}`}
            onClick={clickHandler}
        >
            <Text {...styles.linkText}>{children}</Text>
        </Flex>
    )
}

export {MenuButton}
